import axios, { AxiosError, AxiosResponse, } from "axios";

import { WithProductMetadata } from "@/formTypes/eInvoice/ProductMetadaType"
import { WithInvoiceMetadata } from "@/formTypes/eInvoice/InvoiceMetadaType"
import { SuscriptionDataType } from "@/api/SuscriptionsApi"
import { ClientDataType } from "@/api/ClientApi"
import { InvoiceDataType } from "@/api/InvoiceApi"
import { SalemanDataType } from "@/api/SalemenApi"
import { safeEncodeBase64 } from "@utils/Utils";
import { sanitizeSafeTextDeep } from "@amplifyShared/safeText";
import { WithNoteMetadata } from "@/formTypes/eInvoice/NoteMetadaType";
import { CreditnoteDataType } from "@/api/CreditnoteApi";
import { DebitnoteDataType } from "@/api/DebitnoteApi";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";

export type ATVResponseBody = {
    status: number,
    message: string,
    awsResponse?: string
    awsBody?: string
}
export type InfoRefElement = {
    numero: string,
    fechaEmisionUTC: string,
    tipoDoc: string,
    codigo: string,
    razon: string
}

export type EdocumentPaymentWay = {
    tipo: string,
    medioPagoOtros?: string,
    monto: number,
}

/** Nodo "IdentificacionTercero" del complexType OtrosCargosType (XSD v4.4). */
export type OtrosCargosIdentificacion = {
    tipo: string,
    numero: string,
}

/**
 * Nodo "OtrosCargos" del XSD v4.4. Es hermano de DetalleServicio y va justo antes
 * de ResumenFactura. Los campos opcionales se omiten, nunca se envian vacios: el
 * XSD les exige minLength y un nodo vacio hace que Hacienda rechace el comprobante.
 */
export type OtrosCargosElement = {
    tipoDocumentoOC: string,
    tipoDocumentoOTROS?: string,
    identificacionTercero?: OtrosCargosIdentificacion,
    nombreTercero?: string,
    detalle: string,
    porcentajeOC?: number,
    montoCargo: number,
}

export type DocumentMetadata = {
    fechaEmisionUTC: string,
    calltime: number,
    documentType: string,
    condicionVenta: string,
    clave: string,
    numeroConsecutivo: string,
    atvEndpoint: string,
    InfoRef: InfoRefElement[],
    medioPago: EdocumentPaymentWay[],
    codigoMoneda: string,
    documentVersion: string,
    /**
     * Opcional a proposito: la lambda y el front se despliegan por separado, asi que
     * un bundle viejo del front no debe romper a la lambda nueva.
     */
    OtrosCargos?: OtrosCargosElement[]
}

export type ApiEInvoiceRequest = {
    Suscription: Partial<SuscriptionDataType>,
    Metadata: DocumentMetadata,
    Client?: Partial<ClientDataType>,
    InvoiceProduct: WithProductMetadata<EDocumentProductDataType>[],
    Invoice: WithInvoiceMetadata<InvoiceDataType>,
    Saleman?: SalemanDataType
}

export type ApiENoteRequest = {
    Suscription: Partial<SuscriptionDataType>,
    Metadata: DocumentMetadata,
    Client: Partial<ClientDataType>,
    Invoice: WithInvoiceMetadata<InvoiceDataType>,
    InvoiceNoteProduct: WithProductMetadata<EDocumentProductDataType>[],
    InvoiceNote: WithNoteMetadata<CreditnoteDataType | DebitnoteDataType>,
    Saleman?: SalemanDataType
}

export type MessageMetadata = {
    id: string,
    clave: string,
    numeroCedulaEmisor: string,
    fechaEmisionDoc: string,
    mensaje: string,
    detalleMensaje: string,
    montoTotalImpuesto: number,
    codigoActividad: string,
    condicionImpuesto: string,
    montoTotalImpuestoAcreditar: number,
    montoTotalDeGastoAplicable: number,
    totalFactura: number,
    numeroCedulaReceptor: string,
    numeroConsecutivoReceptor: string,
    tipo_identificacion: string,
    identificacion: string,
    metadata: Partial<DocumentMetadata>
}

const EINVOICES_API_URL = "https://api.easyposcr.com/v4-4/einvoice"
const EINVOICES_API_URL_SANDBOX = "https://api.easyposcr.com/sandbox_4-4/einvoice"
type HttpMethod = "post" | "get" | "put" | "delete";

type EdocumentStatusCode = "pending" | "aceptado" | "procesando" | "rechazado"
export const EDOCUMENT_ATV_STATUS_PENDING: EdocumentStatusCode = "pending";
export const EDOCUMENT_ATV_STATUS_ACEPTADO: EdocumentStatusCode = "aceptado";
export const EDOCUMENT_ATV_STATUS_PROCESANDO: EdocumentStatusCode = "procesando";
export const EDOCUMENT_ATV_STATUS_RECHAZADO: EdocumentStatusCode = "rechazado";

export const EDOCUMENT_ATV_STATUS: Record<EdocumentStatusCode, string> = {
    [EDOCUMENT_ATV_STATUS_PENDING]: "Pendiente Respuesta",
    [EDOCUMENT_ATV_STATUS_ACEPTADO]: "Aceptada",
    [EDOCUMENT_ATV_STATUS_PROCESANDO]: "Procesando",
    [EDOCUMENT_ATV_STATUS_RECHAZADO]: "Rechazada"
}
export const EDOCUMENT_ATV_STATUS_COLORS: Record<EdocumentStatusCode, string> = {
    [EDOCUMENT_ATV_STATUS_PENDING]: "text-warning",
    [EDOCUMENT_ATV_STATUS_ACEPTADO]: "text-success",
    [EDOCUMENT_ATV_STATUS_PROCESANDO]: "text-warning",
    [EDOCUMENT_ATV_STATUS_RECHAZADO]: "text-danger"
}

type EdocumentSubmitionStatus = "not_send" | "send_error" | "sent_with_errors" | "sent"
export const EDOCUMENT_SUBMITION_STATUS_NOT_SEND: EdocumentSubmitionStatus = "not_send";
export const EDOCUMENT_SUBMITION_STATUS_SEND_ERROR: EdocumentSubmitionStatus = "send_error";
export const EDOCUMENT_SUBMITION_STATUS_SENT_WITH_ERRORS: EdocumentSubmitionStatus = "sent_with_errors";
export const EDOCUMENT_SUBMITION_STATUS_SENT: EdocumentSubmitionStatus = "sent";

export const EDOCUMENT_SUBMITION_STATUS_CODE: Record<EdocumentSubmitionStatus, number> = {
    [EDOCUMENT_SUBMITION_STATUS_NOT_SEND]: 0,
    [EDOCUMENT_SUBMITION_STATUS_SEND_ERROR]: 1,
    [EDOCUMENT_SUBMITION_STATUS_SENT_WITH_ERRORS]: 2,
    [EDOCUMENT_SUBMITION_STATUS_SENT]: 3
}
export const EDOCUMENT_SUBMITION_STATUS_MESSAGES: Record<EdocumentSubmitionStatus, string> = {
    [EDOCUMENT_SUBMITION_STATUS_NOT_SEND]: "El comprobante no ha sido enviado",
    [EDOCUMENT_SUBMITION_STATUS_SEND_ERROR]: "Error al intentar enviar el comprobante",
    [EDOCUMENT_SUBMITION_STATUS_SENT_WITH_ERRORS]: "Comprobante Envio pero con errores",
    [EDOCUMENT_SUBMITION_STATUS_SENT]: "Comprobante enviado correctamente"
}

/**
 * Resume el fallo de un envio en algo que se pueda guardar en el documento y mostrar al
 * usuario: el mensaje de axios, el codigo HTTP y el cuerpo con el que respondio el API
 * Gateway (que es donde viene el detalle de un `input` que Step Functions no pudo
 * deserializar).
 */
const describeSubmitError = (error: unknown): Pick<ATVResponseBody, "message" | "awsResponse"> => {
    // Se mira la forma del error en vez de usar `axios.isAxiosError`: el helper vive en el
    // modulo y no en la instancia, asi que un error reenviado o un axios mockeado en los
    // tests no lo tendrian.
    const axiosError = error as Partial<AxiosError>;
    if (!axiosError?.isAxiosError) {
        return { message: error instanceof Error ? error.message : `${error}` };
    }

    const serverBody = axiosError.response?.data;
    const serverBodyText = typeof serverBody === "string"
        ? serverBody
        : serverBody === undefined
            ? ""
            : stringifyOrEmpty(serverBody);

    const message = [
        axiosError.response?.status ? `HTTP ${axiosError.response.status}` : "",
        axiosError.message,
        serverBodyText,
    ].filter(Boolean).join(" | ");

    return {
        message,
        awsResponse: serverBodyText ? safeEncodeBase64(serverBodyText) : undefined,
    };
}

const stringifyOrEmpty = (value: unknown): string => {
    try {
        return JSON.stringify(value) ?? "";
    } catch {
        return "";
    }
}

export class EasyposEDocumentApi {
    constructor(protected suscription: SuscriptionDataType) {
    }

    async submitDocument(document: any, path: string = "", method: HttpMethod): Promise<ATVResponseBody> {
        const response: ATVResponseBody = {
            status: EDOCUMENT_SUBMITION_STATUS_CODE["not_send"]
        } as ATVResponseBody;

        try {
            const baseUrl = this.suscription.einvoice_sandbox ? EINVOICES_API_URL_SANDBOX : EINVOICES_API_URL;
            const apiUrl = `${baseUrl}${path}`;

            // Ultima linea de defensa antes de serializar: el endpoint es un API Gateway
            // con integracion directa a Step Functions, y su plantilla de mapeo interpola
            // este JSON dentro de un literal de JavaScript. Un backtick, una barra
            // invertida, un apostrofo recto o un surrogate huerfano en cualquier texto del
            // payload (no solo en la descripcion del producto) hacen que la ejecucion no
            // arranque por un error de serializacion.
            const data = JSON.stringify(sanitizeSafeTextDeep(document));
            response.awsBody = safeEncodeBase64(data)

            // Request con Axios
            const apiResponse: AxiosResponse = await axios.request({
                url: apiUrl,
                method,
                headers: {
                    "Content-Type": "application/json",
                },
                data
            });

            if (apiResponse.status === 200) {
                response.awsResponse = safeEncodeBase64(JSON.stringify(apiResponse.data));

                if (apiResponse.data?.executionArn) {
                    response.status = EDOCUMENT_SUBMITION_STATUS_CODE["sent"]
                } else {
                    response.status = EDOCUMENT_SUBMITION_STATUS_CODE["sent_with_errors"]
                }
            } else {
                response.status = EDOCUMENT_SUBMITION_STATUS_CODE["send_error"]
            }

        } catch (error) {
            console.error("Error enviando documento:", error);
            response.status = EDOCUMENT_SUBMITION_STATUS_CODE["send_error"]
            // La respuesta del servidor es la unica pista de por que el API Gateway
            // rechazo el envio. Antes se descartaba y la UI solo podia mostrar un mensaje
            // generico, asi que la causa real nunca quedaba registrada en el documento.
            Object.assign(response, describeSubmitError(error))
        }
        return response
    }
}